import { useState, useRef } from 'react';
import { ArrowLeft, Upload, Loader2, Plus } from '@/components/icons';
import { uploadCustomIcon, uploadSvgMarkup } from '../services/iconUploadService';
import { resolveAssetUrl } from '../lib/assets';

interface CustomIconPanelProps {
  /** Commit the chosen icon. Receives the R2 key (upload) or external URL + name. */
  onAdd: (iconId: string, name?: string) => void;
  /** When provided, shows a back button (command-palette flow). Omit for standalone use. */
  onBack?: () => void;
  /** Commit button label. Defaults to "Add to canvas". */
  addLabel?: string;
}

/**
 * Custom-node uploader: Upload / Paste-SVG / Image-URL, with live preview + naming.
 * Shared by the command palette (add-to-canvas) and the My Nodes screen (add-to-library).
 */
export function CustomIconPanel({ onAdd, onBack, addLabel = 'Add to canvas' }: CustomIconPanelProps) {
  const [mode, setMode] = useState<'upload' | 'svg' | 'url'>('upload');
  const [uploadedKey, setUploadedKey] = useState<string | null>(null);
  const [svgValue, setSvgValue] = useState('');
  const [urlValue, setUrlValue] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const previewSrc =
    mode === 'upload'
      ? resolveAssetUrl(uploadedKey)
      : mode === 'url'
        ? urlValue.trim() || null
        : svgValue.trim()
          ? `data:image/svg+xml;utf8,${encodeURIComponent(svgValue.trim())}`
          : null;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    // Pre-fill the name from the filename (minus extension) if the user hasn't typed one.
    setName((n) => n || file.name.replace(/\.[^.]+$/, ''));
    setBusy(true);
    const res = await uploadCustomIcon(file);
    setBusy(false);
    if (!res.success || !res.key) {
      setError(res.error || 'Upload failed.');
      return;
    }
    setUploadedKey(res.key);
  };

  const handleAdd = async () => {
    setError(null);
    const label = name.trim() || undefined;
    if (mode === 'url') {
      if (urlValue.trim()) onAdd(urlValue.trim(), label);
      return;
    }
    if (mode === 'upload') {
      if (uploadedKey) onAdd(uploadedKey, label);
      return;
    }
    // Paste-SVG: upload the markup to R2, then commit its key.
    setBusy(true);
    const res = await uploadSvgMarkup(svgValue);
    setBusy(false);
    if (!res.success || !res.key) {
      setError(res.error || 'Upload failed.');
      return;
    }
    onAdd(res.key, label);
  };

  const tabCls = (m: typeof mode) =>
    `flex-1 rounded-md px-2 py-1.5 text-[12.5px] font-medium transition-colors ${
      mode === m ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
    }`;

  return (
    <div className="p-3">
      {/* Header */}
      <div className="mb-3 flex items-center gap-2">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to search"
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <span className="text-[13px] font-semibold">Add a custom node</span>
      </div>

      {/* Mode tabs */}
      <div className="mb-3 flex gap-1 rounded-lg bg-muted/40 p-1">
        <button type="button" className={tabCls('upload')} onClick={() => { setMode('upload'); setError(null); }}>Upload</button>
        <button type="button" className={tabCls('svg')} onClick={() => { setMode('svg'); setError(null); }}>Paste SVG</button>
        <button type="button" className={tabCls('url')} onClick={() => { setMode('url'); setError(null); }}>Image URL</button>
      </div>

      {/* Input per mode */}
      {mode === 'upload' && (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/svg+xml,image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border py-6 text-[13px] text-muted-foreground hover:border-primary/50 hover:text-foreground disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {busy ? 'Uploading…' : uploadedKey ? 'Choose a different file' : 'Choose an SVG or PNG (max 1MB)'}
          </button>
        </div>
      )}

      {mode === 'svg' && (
        <textarea
          value={svgValue}
          onChange={(e) => setSvgValue(e.target.value)}
          placeholder="Paste <svg>…</svg> markup"
          spellCheck={false}
          className="h-28 w-full resize-none rounded-lg border border-border bg-background p-2.5 font-mono text-[12px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      )}

      {mode === 'url' && (
        <input
          type="url"
          value={urlValue}
          onChange={(e) => setUrlValue(e.target.value)}
          placeholder="https://example.com/logo.svg"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      )}

      {/* Name */}
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Node name (e.g. Payments Service)"
        className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
      />

      {/* Preview + add */}
      <div className="mt-3 flex items-center gap-3">
        <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-border bg-card">
          {previewSrc ? (
            <img src={previewSrc} alt="" width={26} height={26} style={{ objectFit: 'contain' }} />
          ) : (
            <Plus className="h-4 w-4 text-muted-foreground/50" />
          )}
        </span>
        <button
          type="button"
          onClick={handleAdd}
          disabled={busy || !previewSrc}
          className="ss-press ml-auto flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-[13px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {addLabel}
        </button>
      </div>

      {error && <p className="mt-2 text-[12.5px] text-destructive">{error}</p>}
      <p className="mt-2 text-[11.5px] text-muted-foreground/80">
        Uploads and pasted SVG are stored to your account (sign-in required). Image URLs are used as-is.
      </p>
    </div>
  );
}
